import "@complex/PubSub";
import "react-toastify/dist/ReactToastify.css";

import { CircularProgress } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { accountRoutes } from "@root/Account";
import { myActionsRoute } from "@root/Action";
import { candidatesRoutes } from "@root/Candidate";
import { enterprisesRoutes } from "@root/Enterprise";
import { offersRoutes } from "@root/Offer";
import { staticsRoutes } from "@root/Statics";
import { statisticsRoutes } from "@root/Statistics";
import { OOPS_TAG, isChunkLoadError } from "@root/utils/sentry";
import * as Sentry from "@sentry/react";
import { GlobalModalConfirmation, Toast, themeBBJ } from "@styleguide";
import { isAxiosError } from "axios";
import { Suspense, lazy } from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { diagnosisRoutes } from "./Diagnosis";
import { exportsRoutes } from "./Exports";
import { myHomePageRoutes } from "./HomePage";
import { onboardingRoutes } from "./Onboarding";
import { prescriberRoutes } from "./Prescriber";
import { settingsRoutes } from "./Setting";
import { structurePartnersRoutes } from "./StructurePartner";
import { supportCourseRoutes } from "./SupportCourse";
import { workshopsRoutes } from "./Workshop";
import { isReloadingOnNewVersion } from "./utils/reloadOnNewVersion";

const IGNORED_ERRORS: (string | RegExp)[] = [/ResizeObserver/i, "getInternalRouterError"];

// Replaces Sentry's `ignoreErrors` option, which can't be bypassed per event (see OOPS_TAG in beforeSend)
const isIgnoredError = (event: Sentry.ErrorEvent) =>
  (event.exception?.values ?? []).some(({ type, value }) =>
    IGNORED_ERRORS.some((pattern) => {
      const message = `${type}: ${value}`;
      return typeof pattern === "string" ? message.includes(pattern) : pattern.test(message);
    })
  );

Sentry.init({
  dsn: "https://92a399db37eef0e1bb88e43d97c1df00@o4507814360121344.ingest.de.sentry.io/4507821234913360",
  environment: import.meta.env.MODE,
  enabled: import.meta.env.PROD && import.meta.env.MODE !== "develop",
  autoSessionTracking: false,
  beforeSend: (event, hint) => {
    const error = hint.originalException;
    // The page is being reloaded to get the new version: errors in between are expected
    if (isReloadingOnNewVersion()) {
      return null;
    }
    // One issue for all deploys, instead of one per missing chunk name
    if (isChunkLoadError(error)) {
      event.fingerprint = ["chunk-load-error"];
    }
    // Errors displayed on an "Oops" screen are always reported
    if (event.tags?.[OOPS_TAG]) {
      return event;
    }
    // 5xx and 403 are reported by the axios interceptor, the rest (canceled, network, timeout, other 4xx) is expected
    if (isAxiosError(error)) {
      return null;
    }
    return isIgnoredError(event) ? null : event;
  },
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const Navbar = lazy(() => import("@root/Navbar"));
const ErrorPage = lazy(() => import("@root/error"));

const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: <Navbar />,
    errorElement: <ErrorPage />,
    children: [
      ...candidatesRoutes,
      ...myActionsRoute,
      ...enterprisesRoutes,
      ...offersRoutes,
      ...settingsRoutes,
      ...staticsRoutes,
      ...statisticsRoutes,
      ...workshopsRoutes,
      ...supportCourseRoutes,
      ...myHomePageRoutes,
      ...structurePartnersRoutes,
      ...exportsRoutes,
    ],
  },
  {
    path: "/",
    errorElement: <ErrorPage />,
    children: [...diagnosisRoutes],
  },
  ...accountRoutes,
  ...onboardingRoutes,
  ...prescriberRoutes,
]);

export function App() {
  return (
    <ThemeProvider theme={themeBBJ}>
      <CssBaseline />
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <Toast />
      <GlobalModalConfirmation>
        <Suspense fallback={<CircularProgress sx={{ margin: "50vh auto 0", transform: "translate(-50%)" }} />}>
          <RouterProvider router={router} />
        </Suspense>
      </GlobalModalConfirmation>
    </ThemeProvider>
  );
}
